import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, Flex, Text, Button, Avatar, VStack, HStack, useToast, Badge, IconButton
} from '@chakra-ui/react';
import { MdFiberManualRecord, MdStop, MdPause, MdPlayArrow } from 'react-icons/md';

export default function CustomMeetingLayout({ socket, roomName, secretKey, user }) {
  const toast = useToast();
  const [pendingRequests, setPendingRequests] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isLocalRecording, setIsLocalRecording] = useState(false);
  const [isLocalRecordingPaused, setIsLocalRecordingPaused] = useState(false);

  const mediaRecorderRef = useRef(null);
  const screenStreamRef = useRef(null);
  const micStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    if (!socket) return;

    socket.on("admission_request_received", (data) => {
      setPendingRequests(prev => [...prev, data]);
      toast({ title: "طلب انضمام جديد", status: "info", duration: 3000, position: "top-right", isClosable: true });
    });

    socket.on("admission_handled", (data) => {
      setPendingRequests(prev => prev.filter(req => req.socketId !== data.targetSocketId));
    });

    socket.on("room_state_update", (state) => {
      setIsRecording(state.isRecording);
    });

    socket.on("recording_state_changed", (state) => {
      setIsRecording(state.isRecording);
      toast({ 
        title: state.isRecording ? "بدأ التسجيل" : "توقف التسجيل", 
        status: state.isRecording ? "success" : "warning", 
        duration: 2000,
        position: "top-right"
      });
    });

    return () => {
      socket.off("admission_request_received");
      socket.off("admission_handled");
      socket.off("room_state_update");
      socket.off("recording_state_changed");
    };
  }, [socket, toast]);

  // Clean up recording streams and contexts on unmount
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (screenStreamRef.current) {
        screenStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(track => track.stop());
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, []);

  const handleAdmit = (req, approved) => {
    socket.emit("handle_admission", { roomName, targetSocketId: req.socketId, approved, secretKey });
    setPendingRequests(prev => prev.filter(r => r.socketId !== req.socketId));
  };

  const startRecording = async () => {
    let screenStream = null;
    let micStream = null;
    let audioContext = null;

    try {
      // 1. Capture screen video and tab/system audio
      screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: { cursor: "always" },
        audio: true
      });
    } catch (err) {
      console.error("Error capturing screen:", err);
      toast({
        title: "تعذر بدء تسجيل الشاشة",
        description: "يرجى منح الإذن لمشاركة الشاشة لتسجيل الحصة.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right"
      });
      return;
    }

    try {
      const audioTracks = [];
      const screenAudioTracks = screenStream.getAudioTracks();

      // 2. Try to capture microphone audio
      try {
        micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (micErr) {
        console.warn("No mic access or mic not available:", micErr);
      }

      const micAudioTracks = micStream ? micStream.getAudioTracks() : [];

      // 3. Mix screen (system) audio and mic audio if either is present
      if (screenAudioTracks.length > 0 || micAudioTracks.length > 0) {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        const destination = audioContext.createMediaStreamDestination();

        if (screenAudioTracks.length > 0) {
          const screenSource = audioContext.createMediaStreamSource(new MediaStream([screenAudioTracks[0]]));
          screenSource.connect(destination);
        }

        if (micAudioTracks.length > 0) {
          const micSource = audioContext.createMediaStreamSource(new MediaStream([micAudioTracks[0]]));
          micSource.connect(destination);
        }

        audioTracks.push(destination.stream.getAudioTracks()[0]);
      }

      // 4. Combine screen video track with mixed audio track
      const videoTrack = screenStream.getVideoTracks()[0];
      const combinedStream = new MediaStream([videoTrack, ...audioTracks]);

      screenStreamRef.current = screenStream;
      micStreamRef.current = micStream;
      audioContextRef.current = audioContext;

      chunksRef.current = [];

      // 5. Select compatible MIME type
      let options = { mimeType: 'video/webm;codecs=vp9,opus' };
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options = { mimeType: 'video/webm;codecs=vp8,opus' };
      }
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options = { mimeType: 'video/webm' };
      }
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options = { mimeType: 'video/mp4' };
      }
      if (!MediaRecorder.isTypeSupported(options.mimeType)) {
        options = {};
      }

      const mediaRecorder = new MediaRecorder(combinedStream, options);
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mediaRecorder.mimeType || 'video/webm' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.style.display = 'none';
        a.href = url;
        const dateStr = new Date().toISOString().replace(/T/, '_').replace(/\..+/, '').replace(/:/g, '-');
        a.download = `تسجيل_حصة_${roomName || 'meeting'}_${dateStr}.${mediaRecorder.mimeType?.includes('mp4') ? 'mp4' : 'webm'}`;
        a.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }, 100);

        toast({
          title: "تم حفظ التسجيل محلياً",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right"
        });
      };

      // Native browser "Stop sharing" event listener
      videoTrack.onended = () => {
        stopRecording();
      };

      mediaRecorder.start(1000);
      setIsLocalRecording(true);

      // Broadcast state to other room participants
      if (socket) {
        socket.emit("toggle_recording", { roomName, isRecording: true });
      }

      toast({
        title: "بدأ تسجيل الشاشة محلياً",
        status: "info",
        duration: 3000,
        isClosable: true,
        position: "top-right"
      });

    } catch (recorderErr) {
      console.error("Error setting up recorder:", recorderErr);
      toast({
        title: "فشل بدء التسجيل",
        description: recorderErr.message,
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right"
      });
      if (screenStream) screenStream.getTracks().forEach(t => t.stop());
      if (micStream) micStream.getTracks().forEach(t => t.stop());
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    if (screenStreamRef.current) {
      screenStreamRef.current.getTracks().forEach(track => track.stop());
      screenStreamRef.current = null;
    }

    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach(track => track.stop());
      micStreamRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    setIsLocalRecording(false);
    setIsLocalRecordingPaused(false);

    // Broadcast state to other room participants
    if (socket) {
      socket.emit("toggle_recording", { roomName, isRecording: false });
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsLocalRecordingPaused(true);
      toast({
        title: "تم إيقاف التسجيل مؤقتاً",
        status: "info",
        duration: 2000,
        position: "top-right"
      });
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsLocalRecordingPaused(false);
      toast({
        title: "تم استئناف التسجيل",
        status: "info",
        duration: 2000,
        position: "top-right"
      });
    }
  };

  const handlePauseResumeRecording = () => {
    if (isLocalRecordingPaused) {
      resumeRecording();
    } else {
      pauseRecording();
    }
  };

  const handleToggleRecording = () => {
    if (isLocalRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  return (
    <Box className="my-custom-meeting-layout" position="absolute" top={0} left={0} right={0} bottom={0} pointerEvents="none" fontFamily="'Tajawal', sans-serif" zIndex={50}>
      
      {/* Custom Overlays (Absolutely positioned on top of VideoConference) */}
      
      {/* 1. Top Left: Recording Status & Toggle */}
      <Box position="absolute" top={4} left={4} zIndex={100} pointerEvents="auto">
        {user?.role === 'admin' || user?.role === 'instructor' ? (
          <HStack spacing={2} bg="rgba(0,0,0,0.6)" px={3} py={2} borderRadius="md" backdropFilter="blur(5px)">
            <IconButton 
              icon={isLocalRecording ? <MdStop /> : <MdFiberManualRecord />} 
              size="sm"
              colorScheme={isLocalRecording ? "red" : "gray"}
              variant="ghost"
              color={isLocalRecording ? "red.400" : "white"}
              onClick={handleToggleRecording}
              aria-label={isLocalRecording ? "Stop Recording" : "Start Recording"}
              isRound
              animation={isLocalRecording && !isLocalRecordingPaused ? "pulse 1.5s infinite" : "none"}
              sx={{ '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } } }}
            />
            {isLocalRecording && (
              <IconButton
                icon={isLocalRecordingPaused ? <MdPlayArrow /> : <MdPause />}
                size="sm"
                colorScheme="gray"
                variant="ghost"
                color="white"
                onClick={handlePauseResumeRecording}
                aria-label={isLocalRecordingPaused ? "Resume Recording" : "Pause Recording"}
                isRound
              />
            )}
            {isLocalRecording && (
              <Text fontSize="sm" color="red.300" fontWeight="bold">
                {isLocalRecordingPaused ? "التسجيل مؤقت" : "جاري التسجيل محلياً"}
              </Text>
            )}
            {!isLocalRecording && isRecording && (
              <Text fontSize="sm" color="red.300" fontWeight="bold">جاري التسجيل (عبر المشاركين)</Text>
            )}
            {!isLocalRecording && !isRecording && (
              <Text fontSize="sm" color="gray.300">التسجيل متوقف</Text>
            )}
          </HStack>
        ) : (
          isRecording && (
            <HStack spacing={2} bg="rgba(0,0,0,0.6)" px={3} py={2} borderRadius="md" backdropFilter="blur(5px)">
              <Box 
                w={3} 
                h={3} 
                borderRadius="full" 
                bg="red.500" 
                animation="pulse 1.5s infinite"
                sx={{ '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } } }}
              />
              <Text fontSize="sm" color="red.300" fontWeight="bold">جاري تسجيل الحصة...</Text>
            </HStack>
          )
        )}
      </Box>

      {/* 2. Top Right: Pending Admission Requests */}
      {pendingRequests.length > 0 && (
        <Box position="absolute" top={{ base: 16, md: 4 }} right={{ base: 2, md: 4 }} zIndex={100} w={{ base: "calc(100vw - 16px)", md: "300px" }} maxW="400px" pointerEvents="auto">
          <Box bg="white" borderRadius="lg" shadow="xl" overflow="hidden" border="1px solid" borderColor="orange.200">
            <Flex bg="orange.50" p={3} borderBottom="1px solid" borderColor="orange.100" justify="space-between" align="center">
              <Text fontSize="sm" color="orange.700" fontWeight="bold">
                طلبات الانضمام
              </Text>
              <Badge colorScheme="orange" borderRadius="full">{pendingRequests.length}</Badge>
            </Flex>
            <VStack maxH="300px" overflowY="auto" p={2} spacing={2} bg="white" sx={{ '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none' }}>
              {pendingRequests.map((req, idx) => (
                <Flex key={idx} w="full" align="center" justify="space-between" p={2} bg="gray.50" borderRadius="md">
                  <HStack>
                    <Avatar size="sm" name={req.user?.username || "طالب"} />
                    <Text fontSize="sm" color="gray.800" fontWeight="medium">{req.user?.username || "طالب"}</Text>
                  </HStack>
                  <HStack>
                    <Button size="xs" colorScheme="blue" onClick={() => handleAdmit(req, true)}>قبول</Button>
                    <Button size="xs" colorScheme="red" variant="ghost" onClick={() => handleAdmit(req, false)}>رفض</Button>
                  </HStack>
                </Flex>
              ))}
            </VStack>
          </Box>
        </Box>
      )}

    </Box>
  );
}
